import { useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { broadcast } from "@/lib/windowSync";
import AuswahlFeld from "@/components/AuswahlFeld";

/**
 * Gemeinsame Stelle (EA-004) fuer den Folgeprozess nach Kundenanlage:
 * "Jetzt ein Projekt erstellen?" - wird sowohl von der Mail-Anfrage-
 * Uebernahme als auch von "Neuer Kunde" genutzt, damit beide Wege
 * garantiert denselben Ablauf haben und nicht auseinanderdriften.
 */
// A-011 (17.10.2026): Statt nur „Ja/Nein“ zeigt die Frage gleich das vorausgefüllte Projekt
// (Titel, Kategorie aus der Anfrage – Liste kunden_kategorie –, Anliegen als Beschreibung).
// Ralph kann alles ändern; „Ja“ legt das Projekt mit genau diesen Werten an.
export function useProjektErstellenFlow() {
  const [show, setShow] = useState(false);
  const [vorschlag, setVorschlag] = useState(null); // { titel, kategorie, beschreibung, kategorie_quelle, hat_projekte }
  const [werte, setWerte] = useState({ titel: "", kategorie: "", beschreibung: "" });
  const resolver = useRef(null);

  // opts.mailId (A-010 M3d): Projekt aus dieser Mail-Anfrage – Kategorie kommt von dort
  const askAndCreate = async (kundeId, opts = {}) => {
    if (!kundeId) return;
    let v = null;
    try {
      const r = await api.get(`/module-projekte/from-kunde/${kundeId}/vorschlag`, { params: opts.mailId ? { mail_id: opts.mailId } : {} });
      v = r.data || null;
    } catch { /* ohne Vorschlag: Felder leer, Backend belegt beim Anlegen selbst vor */ }
    setVorschlag(v);
    setWerte({ titel: v?.titel || "", kategorie: v?.kategorie || "", beschreibung: v?.beschreibung || "" });
    const confirmed = await new Promise((resolve) => {
      resolver.current = resolve;
      setShow(true);
    });
    if (!confirmed) return;
    try {
      const body = { ...confirmed };
      if (opts.mailId) body.mail_id = opts.mailId;
      const pRes = await api.post(`/module-projekte/erstes-projekt/${kundeId}`, body);
      if (pRes.data.fall === "A" && pRes.data.projekt?.id) {
        toast.success(`Projekt „${pRes.data.projekt.titel}“ angelegt${pRes.data.projekt.kategorie ? ` (Kategorie ${pRes.data.projekt.kategorie})` : ""}`);
        broadcast("arbeitskontext-projekt", { kundeId, projektId: pRes.data.projekt.id });
      }
      if (pRes.data.fall === "B") {
        toast(pRes.data.hinweis);
        broadcast("arbeitskontext-projekt", { kundeId });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fehler beim Erstellen des Projekts");
    }
  };

  const schliessen = (ergebnis) => { setShow(false); resolver.current?.(ergebnis); };
  const upd = (k, v) => setWerte((w) => ({ ...w, [k]: v }));
  const quelleText = { anfrage: "aus der Anfrage", kunde: "aus den Kunden-Kategorien", stichwort: "Stichwort-Treffer im Anliegen" }[vorschlag?.kategorie_quelle] || "";

  const dialog = show ? (
    <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-3 sm:p-4" data-testid="projekt-confirm-dialog">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto p-4 sm:p-5">
        <h3 className="text-lg font-semibold mb-1">Jetzt ein Projekt erstellen?</h3>
        {vorschlag?.hat_projekte ? (
          <p className="text-sm text-muted-foreground mb-4" data-testid="projekt-confirm-hat-projekte">Der Kunde hat schon Projekte. Es wird kein neues angelegt; das vorhandene Projekt wird geöffnet.</p>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">Der Kunde wurde gespeichert. Das Projekt ist vorausgefüllt und kann geändert werden.</p>
            <div className="space-y-3 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" htmlFor="projekt-confirm-titel">Titel</label>
                <input id="projekt-confirm-titel" value={werte.titel} onChange={(e) => upd("titel", e.target.value)}
                  className="w-full h-10 rounded-sm border border-input bg-background px-3 text-sm" data-testid="projekt-confirm-titel" />
              </div>
              <AuswahlFeld docType="kunden_kategorie" label="Kategorie" value={werte.kategorie}
                onChange={(k) => setWerte((w) => ({ ...w, kategorie: k, titel: !w.titel || w.titel === w.kategorie || w.titel === "Neues Projekt" ? (k || "Neues Projekt") : w.titel }))}
                leer="— keine —" testId="projekt-confirm-kategorie"
                hinweis={quelleText && werte.kategorie === vorschlag?.kategorie ? (
                  <div className="mt-1 text-[11px] text-emerald-700" data-testid="projekt-confirm-kategorie-quelle">Kategorie {quelleText}</div>
                ) : null} />
              <div>
                <label className="block text-sm font-medium mb-1.5" htmlFor="projekt-confirm-beschreibung">Beschreibung / Anliegen</label>
                <textarea id="projekt-confirm-beschreibung" value={werte.beschreibung} onChange={(e) => upd("beschreibung", e.target.value)} rows={3}
                  className="w-full rounded-sm border border-input bg-background px-3 py-2 text-sm resize-y" data-testid="projekt-confirm-beschreibung" />
              </div>
            </div>
          </>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => schliessen(false)}
            className="px-4 py-2 text-sm border rounded-sm hover:bg-muted"
            data-testid="btn-projekt-confirm-nein"
          >
            Nein
          </button>
          <button
            type="button"
            onClick={() => schliessen(vorschlag?.hat_projekte ? {} : { titel: werte.titel.trim(), kategorie: werte.kategorie, beschreibung: werte.beschreibung })}
            className="px-4 py-2 text-sm rounded-sm bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="btn-projekt-confirm-ja"
          >
            {vorschlag?.hat_projekte ? "Projekt öffnen" : "Ja, Projekt erstellen"}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { askAndCreate, dialog };
}
